/*======================================================================================================================
  * Bluebell: src/app/state/runs/run.effects.ts
  * Last Edited: 20/01/2025
  * Copyright 2024-2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EffectHandlerService } from '@services/effect-handler.service';
import { programmeProjectSelectionUnapplied } from '@state/account/account.actions';
import { ApiService } from '@state/api.service';
import { CameraSet } from '@state/camera-sets/camera-sets.interfaces';
import { ProjectActions } from '@state/projects/project.actions';
import { RunsService } from '@app/pages/runs/runs-service/runs.service';
import { selectCurrentProject, selectCurrentProjectId } from '@state/projects/project.selectors';
import { RunActions } from '@state/runs/run.actions';
import {
  Run,
  CreateAthleteRunData,
  CreateChildRunData,
  CreateRunPartial,
  FullRun,
  UpdateRunPartial, CaseLogs, CFDSetup, Case,
} from '@state/runs/run.interfaces';
import { selectSelectedRun } from '@state/runs/run.selectors';
import { Observable } from 'rxjs';
import { concatMap, filter, map, mergeMap, switchMap } from 'rxjs/operators';


@Injectable()
export class RunEffects {
  private actions$ = inject(Actions);
  private runService = inject(RunsService);
  private handler = inject(EffectHandlerService);
  private store = inject(Store);
  private api = inject(ApiService);

  selectedProject = this.store.selectSignal(selectCurrentProject);

  create$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.create),
    mergeMap((data: CreateRunPartial) => this.api.createRun(data).pipe(
      this.handler.effectResponse({
        success: (run: FullRun) => RunActions.createSuccess(run),
        retry: RunActions.create(data),
        failure: RunActions.createFailure(),
        failureMessage: 'Could not create run'
      })
    ))
  ));

  createChild$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.createChild),
    mergeMap((data: CreateChildRunData) => this.api.createChildRun(data).pipe(
      this.handler.effectResponse({
        success: (run: FullRun) => RunActions.createChildSuccess(run),
        retry: RunActions.createChild(data),
        failure: RunActions.createChildFailure(),
        failureMessage: 'Could not create child run',
      })
    ))
  ));

  createAthleteRun$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.createAthleteRun),
    mergeMap((data: CreateAthleteRunData) => this.api.createAthleteRun(data).pipe(
      this.handler.effectResponse({
        success: (response: string) => {
          switch (response) {
            case 'Duplicate Pose':
              console.error('ERROR: Duplicate Athlete Pose Run Not Created');
              this.handler.errorMessage(`This athlete pose has already been used, so run ${data.title} has not been created.`);
              return RunActions.createAthleteRunFailure();
            default:
              this.handler.successMessage(`Run ${data.title} is now being created, this may take a few moments.`);
              return RunActions.createAthleteRunSuccess();
          }
        },
        retry: RunActions.createAthleteRun(data),
        failure: RunActions.createAthleteRunFailure(),
        failureMessage: 'Could not create athlete run.'
      }),
    ))
  ));

  retrieve$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.retrieve, ProjectActions.selectSuccess),
    switchMap(() => this.api.listRuns(this.selectedProject()?.uuid).pipe(
      this.handler.effectResponse({
        success: (runs: Run[]) => RunActions.retrieveSuccess(runs),
        retry: RunActions.retrieve(),
        failure: RunActions.retrieveFailure(),
        failureMessage: 'Could not load runs'
      })
    ))
  ));

  clearExistingOnRetrieve$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.retrieve),
    map(() => RunActions.clearRuns()),
  ));

  clearExistingOnAccountChange$ = createEffect(() => this.actions$.pipe(
    ofType(programmeProjectSelectionUnapplied),
    map(() => RunActions.clearRuns())
  ));

  reloadIfSelected$ = createEffect(() => this.actions$.pipe(
    ofType(
      RunActions.reloadIfSelected,
      RunActions.archiveSuccess,
      RunActions.generateCameraSetSuccess,
    ),
    concatLatestFrom(() => this.store.select<FullRun|null>(selectSelectedRun)),
    filter(([{ runUuid }, selectedRun]) => !!runUuid && !!selectedRun && runUuid === selectedRun.uuid),
    map(([, selectedRun]) => RunActions.select(selectedRun as FullRun))
  ));

  updateIfSelected$ = createEffect(() => this.actions$.pipe(
    ofType(
      RunActions.editTagsSuccess,
      RunActions.editParametersSuccess,
    ),
    concatLatestFrom(() => this.store.select<FullRun|null>(selectSelectedRun)),
    filter(([{ runUuid }, selectedRun]) => !!runUuid && !!selectedRun && runUuid === selectedRun.uuid),
    map(([{
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      runUuid,
      ...responseData
    }]) =>
      RunActions.updateIfSelected({ ...responseData })
    )
  ));

  update$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.update),
    concatMap((data: UpdateRunPartial) => this.api.updateRun(data).pipe(
      this.handler.effectResponse({
        success: (run: FullRun) => RunActions.updateSuccess(run),
        retry: RunActions.update(data),
        failure: RunActions.updateFailure(),
        failureMessage: 'Could not update run'
      }),
    ))
  ));

  updateParts$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.updateParts),
    concatMap(({ uuid, parts }) => this.api.updateRun({ uuid, parts }).pipe(
      this.handler.effectResponse({
        success: (run: FullRun) => RunActions.updatePartsSuccess(run),
        retry: RunActions.updateParts({ uuid, parts }),
        failure: RunActions.updatePartsFailure(),
        failureMessage: 'Could not update run parts'
      }),
    ))
  ));

  delete$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.delete),
    concatMap((run) => this.api.deleteRun(run.uuid).pipe(
      this.handler.effectResponse({
        success: () => RunActions.deleteSuccess(run),
        retry: RunActions.delete(run),
        failure: RunActions.deleteFailure(),
        failureMessage: 'Could not delete run'
      }),
    ))
  ));

  select$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.select),
    concatLatestFrom(() => this.store.select(selectCurrentProjectId)),
    switchMap(([{ uuid }, projectUUID]: [{uuid: string}, string|null]) => this.api.retrieveRun(uuid).pipe(
      this.handler.effectResponse({
        success: (fullRun: FullRun) => RunActions.selectSuccess(fullRun, projectUUID as string),
        retry: RunActions.select({ uuid }),
        failure: RunActions.selectFailure(),
        failureMessage: 'Could not select run'
      })
    ))
  ));

  lifecycleUpdate$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.lifecycleUpdate),
    concatLatestFrom(() => [
      this.store.select<FullRun|null>(selectSelectedRun),
      this.store.select<string|null>(selectCurrentProjectId)
    ]),
    filter(([{ runUUID }, selectedRun]) => runUUID === selectedRun?.uuid),
    switchMap(([{ runUUID }, , projectUUID]) => this.api.retrieveRun(runUUID).pipe(
      this.handler.effectResponse({
        success: (selectedRun: FullRun) => RunActions.lifecycleUpdateSuccess(selectedRun, projectUUID as string),
        retry: RunActions.lifecycleUpdate(runUUID),
        failure: RunActions.lifecycleUpdateFailure(),
        failureMessage: 'Error updating Run Lifecycle',
      })
    ))
  ));

  /* Other Run Actions */

  archive$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.archive),
    concatMap((run: Run) => this.api.runArchive(run.uuid).pipe(
      this.handler.effectResponse({
        success: () => RunActions.archiveSuccess(run.uuid),
        retry: RunActions.archive(run),
        failure: RunActions.archiveFailure(),
        failureMessage: 'Could not archive run'
      }),
    ))
  ));

  importRun$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.importRun),
    concatMap(({ project, run_uuids, symlink }) => this.api.importRun(project, run_uuids, symlink).pipe(
      this.handler.effectResponse({
        success: () => RunActions.importRunSuccess({ run_uuids }),
        retry: RunActions.importRun({ project, run_uuids, symlink }),
        failure: RunActions.importRunFailure(),
        failureMessage: 'Could not import run'
      })
    ))
  ));

  getLogs$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.getLogs),
    switchMap((run: Run) => this.api.runListLogs(run.uuid).pipe(
      this.handler.effectResponse({
        success: (logs: CaseLogs[]) => RunActions.getLogsSuccess(logs),
        retry: RunActions.getLogs(run),
        failure: RunActions.getLogsFailure(),
        failureMessage: 'Could not retrieve logs'
      })
    ))
  ));

  editRunTags$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.editTags),
    concatMap(({ run, tags }) => this.api.runUpdateTags(run.uuid, tags).pipe(
      this.handler.effectResponse({
        success: () => RunActions.editTagsSuccess({ runUuid: run.uuid, tags }),
        retry: RunActions.editTags({ run, tags }),
        failure: RunActions.editTagsFailure(),
        failureMessage: 'Could not update tags'
      }),
    ))
  ));

  editRunParameters$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.editParameters),
    concatMap(({ run, parameters }) =>
      this.api.runUpdateParameters(run.uuid, parameters).pipe(
        this.handler.effectResponse({
          success: () => {
            const _parameters: Run['parameters'] = {};
            Object.entries(parameters).forEach(([key, value]) => _parameters[key] = [value]);
            return RunActions.editParametersSuccess({ runUuid: run.uuid, parameters: _parameters });
          },
          retry: RunActions.editParameters({ run, parameters }),
          failure: RunActions.editParametersFailure(),
          failureMessage: 'Could not update run parameters'
        })
    ))
  ));

  generateCameraSet$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.generateCameraSet),
    switchMap((run: Run) => this.api.runGenerateCameraSet(run.uuid).pipe(
      this.handler.effectResponse({
        success: (cameraSet: CameraSet) => RunActions.generateCameraSetSuccess({ runUuid: run.uuid, cameraSet }),
        retry: RunActions.generateCameraSet(run),
        failure: RunActions.generateCameraSetFailure(),
        successMessage: `Camera Set for "${run.name}" generated`,
        failureMessage: 'Could not generate camera set'
      }),
    ))
  ));

  /* Reactive Effects */

  // Clear the selected Run when the project is deselected
  clearSelectedRun$ = createEffect(() => this.actions$.pipe(
    ofType(ProjectActions.deselect),
    map(() => RunActions.selectClear())
  ));

  clearPaginationStateOnProjectSelect$ = createEffect(() => this.actions$.pipe(
    ofType(ProjectActions.select),
    map(() => this.runService.clearTableState()),
  ), {dispatch: false});

  /*
  Case Effects
  */

  caseUpdateCFDSetup$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.caseUpdateCFDSetup),
    mergeMap(({ uuid, postpro, cfd_setup, case_uuid }) => this.api.updateCFDSetup(uuid, cfd_setup).pipe(
      this.handler.effectResponse({
        retry: RunActions.caseUpdateCFDSetup({ uuid, postpro, cfd_setup, case_uuid }),
        success: (updatedSetup: CFDSetup) => RunActions.caseUpdateCFDSetupSuccess({ case_uuid, updatedSetup }),
        failure: RunActions.caseUpdateCFDSetupFailure(),
        successMessage: `${postpro ? 'Postpro' : 'CFD'} Setup updated`,
        failureMessage: 'Could not update CFD Setup'
      })
    ))
  ));

  caseSetChecksTemplates$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.caseSetChecksTemplate),
    mergeMap(({ uuid, name }) => this.api.caseSetCheckTemplate(uuid, name).pipe(
      map((response: FullRun) => RunActions.updateSuccess(response)),
    ))
  ));

  caseLifecycleAction$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.caseLifecycleAction),
    mergeMap(({ uuid, runUUID, action, force, original_action, run_on, extra_exports, move_to_filex }) => {
      const getLifecycleAction = (): Observable<any> => {
        switch (action) {
          case 'abort':
            return this.api.runAbortLifecycle(runUUID, { case: uuid });
          case 'archive':
            return this.api.runArchiveCase(runUUID, uuid);
          case 'delete':
            return this.api.deleteCase(uuid);
          case 'execute':
            return this.api.runExecute(runUUID, { case: uuid });
          case 'reset':
            return this.api.runResetLifecycle(runUUID, { case: uuid });
          case 'skip':
            return this.api.runSkipCurrentAction(runUUID, { case: uuid });
          case 'rerun postpro':
          case 'rerun delta postpro':
            return this.api.runExecute(runUUID, { case: uuid, force, original_action, rerun: true });
          case 'run on':
            return this.api.runRunOn(runUUID, { case: uuid, run_on });
          case 'extra export':
            return this.api.runExtraExports(runUUID, { case: uuid, extra_exports, move_to_filex: !!move_to_filex });
        }
      };
      return getLifecycleAction().pipe(
        this.handler.effectResponse({
          retry: RunActions.caseLifecycleAction({ uuid, runUUID, action, force, original_action, run_on, extra_exports, move_to_filex }),
          success: () => RunActions.caseLifecycleActionSuccess({ runUUID, action }),
          failure: RunActions.caseLifecycleActionFailure(),
          failureMessage: `Could not ${action} case`
        })
      );
    })
  ));

  caseLifecycleActionSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.caseLifecycleActionSuccess),
    map(({ runUUID }) => RunActions.lifecycleUpdate(runUUID))
  ));

  caseGroupDelete$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.caseGroupDelete),
    mergeMap(({ uuids, runUUID }) => this.api.deleteCases(uuids).pipe(
      this.handler.effectResponse({
        retry: RunActions.caseGroupDelete({ uuids, runUUID }),
        success: () => RunActions.caseLifecycleActionSuccess({ runUUID, action: 'delete' }),
        failure: () => RunActions.caseLifecycleActionFailure(),
        failureMessage: 'Could not delete cases',
      })
    ))
  ));

  caseSetMappedInletSource$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.caseSetMappedInletSource),
    switchMap(({ uuid, source }) => this.api.caseSetMappedInletSource(uuid, source).pipe(
      this.handler.effectResponse({
        retry: RunActions.caseSetMappedInletSource({ uuid, source }),
        success: () => RunActions.caseSetMappedInletSourceSuccess(),
        failure: () => RunActions.caseSetMappedInletSourceFailure(),
        failureMessage: 'Could not set mapped inlet source',
      })
    ))
  ));

  caseSetMappedSolutionDonor$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.caseSetMappedSolutionDonor),
    switchMap(({ caseUUID, donorUUID }) => this.api.caseSetMappedSolutionDonor(caseUUID, donorUUID).pipe(
      this.handler.effectResponse({
        success: () => RunActions.caseSetMappedSolutionDonorSuccess(),
        retry: RunActions.caseSetMappedSolutionDonor({ caseUUID, donorUUID }),
        failure: RunActions.caseSetMappedSolutionDonorFailure(),
        failureMessage: 'Could not set mapped solution donor.',
      })
    ))
  ));

  caseEditExtraDeltas$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.caseEditExtraDeltas),
    switchMap(({ uuid, metadata }) => this.api.updateCase(uuid, metadata).pipe(
      this.handler.effectResponse({
        retry: RunActions.caseEditExtraDeltas({ uuid, metadata }),
        success: (attitude: Case) => RunActions.caseEditExtraDeltasSuccess({ attitude }),
        failure: RunActions.caseEditExtraDeltasFailure(),
        failureMessage: 'Could not update extra deltas',
      })
    ))
  ));

  allCasesEditExtraDeltas$ = createEffect(() => this.actions$.pipe(
    ofType(RunActions.allCasesEditExtraDeltas),
    switchMap(({ uuid, metadata }) => this.api.updateAllRunCases(uuid, metadata).pipe(
      this.handler.effectResponse({
        retry: RunActions.allCasesEditExtraDeltas({ uuid, metadata }),
        success: (attitudes: Case[]) => RunActions.allCasesEditExtraDeltasSuccess({ attitudes }),
        failure: RunActions.allCasesEditExtraDeltasFailure(),
        failureMessage: 'Could not update extra deltas',
      })
    ))
  ));
}
